import type { AuthFeatureIcon } from './auth-config';

interface IconProps {
    className?: string;
}

/**
 * Abstract aortic-arch glyph: a stylised curve through three points
 * with a centerline dot pattern. Geometric, not literal.
 */
export function Brandmark({ className }: IconProps) {
    return (
        <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" className={className}>
            <circle cx="16" cy="16" r="15" stroke="currentColor" strokeOpacity="0.25" />
            <path
                d="M6 22 C 10 22, 10 10, 16 10 S 22 22, 26 22"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                fill="none"
            />
            <circle cx="9" cy="22" r="1.3" fill="currentColor" />
            <circle cx="16" cy="10" r="1.3" fill="currentColor" />
            <circle cx="23" cy="22" r="1.3" fill="currentColor" />
        </svg>
    );
}

function IconSegmentation({ className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
            <path
                d="M4 6 L12 3 L20 6 L20 18 L12 21 L4 18 Z"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinejoin="round"
            />
            <path d="M4 6 L12 9 L20 6" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M12 9 L12 21" stroke="currentColor" strokeWidth="1.3" />
            <circle cx="12" cy="12" r="1.6" fill="currentColor" />
        </svg>
    );
}

function IconCenterline({ className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
            <path
                d="M3 19 C 7 19, 8 9, 12 9 S 17 19, 21 19"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
            />
            <circle cx="5" cy="19" r="1.4" fill="currentColor" />
            <circle cx="12" cy="9" r="1.4" fill="currentColor" />
            <circle cx="19" cy="19" r="1.4" fill="currentColor" />
            <path
                d="M5 19 L19 19"
                stroke="currentColor"
                strokeWidth="0.8"
                strokeDasharray="2 2"
                strokeOpacity="0.6"
            />
        </svg>
    );
}

function IconMeasure({ className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
            <rect x="3" y="9" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="1.3" />
            <path
                d="M7 9 V 12 M11 9 V 13 M15 9 V 12 M19 9 V 13"
                stroke="currentColor"
                strokeWidth="1.3"
            />
        </svg>
    );
}

const ICON_BY_KEY: Record<AuthFeatureIcon, (props: IconProps) => React.ReactElement> = {
    segmentation: IconSegmentation,
    centerline: IconCenterline,
    measure: IconMeasure,
};

export function FeatureIcon({ name, className }: { name: AuthFeatureIcon; className?: string }) {
    const Icon = ICON_BY_KEY[name];
    return <Icon className={className} />;
}
